import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { formatMoney } from "../../../utils/helpers";

function statusColor(status) {
    const s = String(status || "").toLowerCase();
    if (s === "processed") return "success";
    if (s === "created") return "info";
    if (s === "refunded" || s === "canceled" || s === "cancelled") return "warning";
    if (s === "failed") return "error";
    return "default";
}

export default function TransactionOrderDetailDialog({ open, transaction, onClose }) {
    const order = transaction?.order || null;
    const items = Array.isArray(order?.items) ? order.items : [];
    const promo = order?.promocode || null;
    const discountAmount = Number(order?.discount_amount || 0);
    const originalAmount = order?.original_amount != null
        ? Number(order.original_amount)
        : Number(order?.amount || 0) + discountAmount;
    const paidAmount = Number(order?.amount ?? transaction?.amount ?? 0);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {order?.id ? `Order #${order.id}` : "Order details"}
                {transaction?.id ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Transaction #{transaction.id}
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent dividers>
                {!order ? (
                    <Typography color="text.secondary">No order linked to this transaction.</Typography>
                ) : (
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Customer
                            </Typography>
                            <Typography variant="body2">
                                {transaction?.user?.full_name || order.customer_name || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {transaction?.user?.email || order.email || "—"}
                                {(transaction?.user?.phone || order.phone)
                                    ? ` • ${transaction?.user?.phone || order.phone}`
                                    : ""}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                color={statusColor(transaction?.status)}
                                label={`TX: ${transaction?.status || "—"}`}
                            />
                            <Chip
                                size="small"
                                color={statusColor(order.payment_status)}
                                label={`Payment: ${order.payment_status || "—"}`}
                            />
                            <Chip size="small" variant="outlined" label={transaction?.method || "—"} />
                        </Stack>

                        {transaction?.transaction_id ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                Gateway: {transaction.transaction_id}
                            </Typography>
                        ) : null}

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Items
                            </Typography>
                            {!items.length ? (
                                <Typography variant="body2" color="text.secondary">
                                    No line items.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Course</TableCell>
                                            <TableCell align="center">Qty</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item) => (
                                            <TableRow key={`${item.item}-${item.item_id}`}>
                                                <TableCell>{item.name || `Item #${item.item_id}`}</TableCell>
                                                <TableCell align="center">{item.quantity ?? 1}</TableCell>
                                                <TableCell align="right">
                                                    {formatMoney(Number(item.price || 0))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>

                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Stack spacing={0.75}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">
                                        Subtotal
                                    </Typography>
                                    <Typography variant="body2">{formatMoney(originalAmount)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Discount
                                        {promo?.code
                                            ? ` (${promo.code}${promo.discount != null ? `, ${promo.discount}%` : ""})`
                                            : ""}
                                    </Typography>
                                    <Typography variant="body2">
                                        {discountAmount > 0 ? `- ${formatMoney(discountAmount)}` : formatMoney(0)}
                                    </Typography>
                                </Stack>
                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="subtitle2">Paid total</Typography>
                                    <Typography variant="subtitle2">{formatMoney(paidAmount)}</Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
